import { Injectable } from '@angular/core';
import { Actions, createEffect } from '@ngrx/effects';
import { NotificationsService } from '@shared/services/notifications.service';
import { PermissionsManagerService } from '../../services/permissions-manager.service';
import { catchError, map, of, switchMap } from 'rxjs';
import { ofType } from '@ngrx/effects';
import { PermissionsManagerActions } from '../actions/actions-type.action';
import { BadResponseWithError } from '@shared/models/bad-response-with-error.interface';
import { IndividualPermissionsResponse } from '../../models/api/response/individual-permissions.response';
import { PlayerProfilesWithPagination } from '../../../user-management/models/api/response/user-management.response';

@Injectable({
    providedIn: 'root',
})
export class PermissionsManagerEffect {
    fetchPermissionsGroupsRequest$ = createEffect(() =>
        this.actions$.pipe(
            ofType(PermissionsManagerActions.fetchPermissionsGroupRequests),
            switchMap(({ payload }) => {
                return this.permissionsManagerService
                    .fetchPermissionsGroupsRequest(payload)
                    .pipe(
                        map((response: any) => {
                            return PermissionsManagerActions.fetchPermissionsGroupRequestsSuccess(
                                {
                                    payload: {
                                        items: response,
                                    },
                                }
                            );
                        }),
                        catchError((response: BadResponseWithError) => {
                            this.notificationsService.showError(
                                response.error.message
                            );

                            return of(
                                PermissionsManagerActions.fetchPermissionsGroupRequestsFailure(
                                    {
                                        payload: response,
                                    }
                                )
                            );
                        })
                    );
            })
        )
    );

    fetchPermissionsRequest$ = createEffect(() =>
        this.actions$.pipe(
            ofType(PermissionsManagerActions.fetchPermissionsRequests),
            switchMap(() => {
                return this.permissionsManagerService
                    .fetchPermissionsRequest()
                    .pipe(
                        map((items: any[]) =>
                            PermissionsManagerActions.fetchPermissionsRequestsSuccess(
                                {
                                    payload: {
                                        items,
                                    },
                                }
                            )
                        ),
                        catchError((response: any) => {
                            this.notificationsService.showError(
                                response.message
                            );

                            return of(
                                PermissionsManagerActions.fetchPermissionsRequestsFailure(
                                    {
                                        payload: response,
                                    }
                                )
                            );
                        })
                    );
            })
        )
    );
    addNewPermissionsGroupRequest$ = createEffect(() =>
        this.actions$.pipe(
            ofType(PermissionsManagerActions.addNewPermissionsGroupRequest),
            switchMap(({ payload }) => {
                return this.permissionsManagerService
                    .addNewPermissionGroup(payload)
                    .pipe(
                        map((newGroup) =>
                            PermissionsManagerActions.addNewPermissionsGroupsSuccess(
                                {
                                    payload: {
                                        newGroup,
                                    },
                                }
                            )
                        ),
                        catchError((response: BadResponseWithError) => {
                            this.notificationsService.showError(
                                response.error.message
                            );

                            return of(
                                PermissionsManagerActions.addNewPermissionsGroupFailure(
                                    {
                                        payload: response,
                                    }
                                )
                            );
                        })
                    );
            })
        )
    );
    removePermissionsGroupRequest$ = createEffect(() =>
        this.actions$.pipe(
            ofType(PermissionsManagerActions.removePermissionsGroupRequest),
            switchMap(({ payload }) => {
                return this.permissionsManagerService
                    .deleteGroup(payload.id)
                    .pipe(
                        map(() =>
                            PermissionsManagerActions.removePermissionsGroupsSuccess(
                                {
                                    payload: {
                                        id: payload.id,
                                    },
                                }
                            )
                        ),
                        catchError((response: BadResponseWithError) => {
                            this.notificationsService.showError(
                                response.error.message
                            );

                            return of(
                                PermissionsManagerActions.removePermissionsGroupFailure(
                                    {
                                        payload: response,
                                    }
                                )
                            );
                        })
                    );
            })
        )
    );
    removeUsersFromPermissionsGroupRequest$ = createEffect(() =>
        this.actions$.pipe(
            ofType(
                PermissionsManagerActions.removeUsersFromPermissionsGroupRequest
            ),
            switchMap(({ payload }) => {
                return this.permissionsManagerService
                    .removeUsersFromGroup(payload.groupId, payload.users)
                    .pipe(
                        map((items: any[]) =>
                            PermissionsManagerActions.removeUsersFromPermissionsGroupSuccess(
                                {
                                    payload: {
                                        items,
                                    },
                                }
                            )
                        ),
                        catchError((response: BadResponseWithError) => {
                            this.notificationsService.showError(
                                response.error.message
                            );

                            return of(
                                PermissionsManagerActions.removeUsersFromPermissionsGroupFailure(
                                    {
                                        payload: response,
                                    }
                                )
                            );
                        })
                    );
            })
        )
    );
    addNewUsersToPermissionGroupRequest$ = createEffect(() =>
        this.actions$.pipe(
            ofType(
                PermissionsManagerActions.addNewUsersToPermissionsGroupRequest
            ),
            switchMap(({ payload }) => {
                return this.permissionsManagerService
                    .addUsersToGroup(payload.groupId, payload.users)
                    .pipe(
                        map((items: any[]) =>
                            PermissionsManagerActions.addNewUsersToPermissionsGroupSuccess(
                                {
                                    payload: {
                                        items,
                                    },
                                }
                            )
                        ),
                        catchError((response: BadResponseWithError) => {
                            this.notificationsService.showError(
                                response.error.message
                            );

                            return of(
                                PermissionsManagerActions.addNewUsersToPermissionsGroupFailure(
                                    {
                                        payload: response,
                                    }
                                )
                            );
                        })
                    );
            })
        )
    );

    addPermissionsToGroup$ = createEffect(() =>
        this.actions$.pipe(
            ofType(PermissionsManagerActions.addPermissionsToGroup),
            switchMap(({ payload }) => {
                return this.permissionsManagerService
                    .addPermissionsToGroup(payload.id, payload.permissions)
                    .pipe(
                        map((items: any) =>
                            PermissionsManagerActions.addPermissionsToGroupSuccess(
                                {
                                    payload: {
                                        items,
                                    },
                                }
                            )
                        ),
                        catchError((response: BadResponseWithError) => {
                            this.notificationsService.showError(
                                response.error.message
                            );

                            return of(
                                PermissionsManagerActions.addPermissionsToGroupFailure(
                                    {
                                        payload: response,
                                    }
                                )
                            );
                        })
                    );
            })
        )
    );

    deletePermissionsFromGroup$ = createEffect(() =>
        this.actions$.pipe(
            ofType(PermissionsManagerActions.deletePermissionsFromGroup),
            switchMap(({ payload }) => {
                return this.permissionsManagerService
                    .deletePermissionsFromGroup(
                        payload.id,
                        payload.permissionIds
                    )
                    .pipe(
                        map((items: any) =>
                            PermissionsManagerActions.deletePermissionsFromGroupSuccess(
                                {
                                    payload: {
                                        items,
                                    },
                                }
                            )
                        ),
                        catchError((response: BadResponseWithError) => {
                            this.notificationsService.showError(
                                response.error.message
                            );

                            return of(
                                PermissionsManagerActions.deletePermissionsFromGroupFailure(
                                    {
                                        payload: response,
                                    }
                                )
                            );
                        })
                    );
            })
        )
    );

    updatePermissionsGroupRequest$ = createEffect(() =>
        this.actions$.pipe(
            ofType(PermissionsManagerActions.updatePermissionsGroupRequest),
            switchMap(({ payload }) => {
                return this.permissionsManagerService
                    .updatePermissionGroup(payload)
                    .pipe(
                        map((items: any) =>
                            PermissionsManagerActions.updatePermissionsGroupSuccess(
                                {
                                    payload: {
                                        items,
                                    },
                                }
                            )
                        ),
                        catchError((response: BadResponseWithError) => {
                            this.notificationsService.showError(
                                response.error.message
                            );

                            return of(
                                PermissionsManagerActions.updatePermissionsGroupFailure(
                                    {
                                        payload: response,
                                    }
                                )
                            );
                        })
                    );
            })
        )
    );

    fetchIndividualPermissionsRequest$ = createEffect(() =>
        this.actions$.pipe(
            ofType(PermissionsManagerActions.fetchIndividualPermissionsRequest),
            switchMap(({ payload }) => {
                return this.permissionsManagerService
                    .fetchIndividualPermissions(
                        payload.profileId,
                        payload.contextEntityType || null,
                        payload?.contextEntityId || null
                    )
                    .pipe(
                        map((response: IndividualPermissionsResponse) =>
                            PermissionsManagerActions.fetchIndividualPermissionsSuccess(
                                {
                                    payload: response,
                                }
                            )
                        ),
                        catchError((response: BadResponseWithError) => {
                            this.notificationsService.showError(
                                response.error.message
                            );

                            return of(
                                PermissionsManagerActions.fetchIndividualPermissionsFailure(
                                    {
                                        payload: response,
                                    }
                                )
                            );
                        })
                    );
            })
        )
    );

    duplicatePermissionGroupRequest$ = createEffect(() =>
        this.actions$.pipe(
            ofType(PermissionsManagerActions.duplicatePermissionGroupRequest),
            switchMap(({ payload }) => {
                return this.permissionsManagerService
                    .duplicatePermissionGroup(payload.id, payload.groupData)
                    .pipe(
                        map((newGroup: any) =>
                            PermissionsManagerActions.duplicatePermissionGroupSuccess(
                                {
                                    payload: {
                                        newGroup,
                                    },
                                }
                            )
                        ),
                        catchError((response: BadResponseWithError) => {
                            this.notificationsService.showError(
                                response.error.message
                            );

                            return of(
                                PermissionsManagerActions.duplicatePermissionGroupFailure(
                                    {
                                        payload: response,
                                    }
                                )
                            );
                        })
                    );
            })
        )
    );

    fetchUsersInGroup$ = createEffect(() =>
        this.actions$.pipe(
            ofType(PermissionsManagerActions.fetchUsersInGroup),
            switchMap(({ groupId, payload }) => {
                return this.permissionsManagerService
                    .fetchUsersInGroup(groupId || '', payload)
                    .pipe(
                        map((response: PlayerProfilesWithPagination) =>
                            PermissionsManagerActions.fetchUsersInGroupSuccess({
                                payload: response,
                            })
                        ),
                        catchError((response: BadResponseWithError) => {
                            this.notificationsService.showError(
                                response.error.message
                            );

                            return of(
                                PermissionsManagerActions.fetchUsersInGroupFailure(
                                    {
                                        payload: response,
                                    }
                                )
                            );
                        })
                    );
            })
        )
    );

    assignIndividualPermission$ = createEffect(() =>
        this.actions$.pipe(
            ofType(PermissionsManagerActions.assignIndividualPermissionRequest),
            switchMap(({ payload }) => {
                return this.permissionsManagerService
                    .assignIndividualPermission(
                        payload.profileId,
                        payload.permissions
                    )
                    .pipe(
                        map((response) => {
                            return PermissionsManagerActions.assignIndividualPermissionSuccess(
                                {
                                    payload: response,
                                }
                            );
                        }),
                        catchError((response: BadResponseWithError) => {
                            this.notificationsService.showError(
                                response.error?.message ||
                                    'Failed to assign individual permission'
                            );
                            return of(
                                PermissionsManagerActions.assignIndividualPermissionFailure(
                                    {
                                        payload: response,
                                    }
                                )
                            );
                        })
                    );
            })
        )
    );

    fetchSummary$ = createEffect(() =>
        this.actions$.pipe(
            ofType(PermissionsManagerActions.fetchSummaryRequest),
            switchMap(({ payload }) => {
                return this.permissionsManagerService
                    .fetchSummary(
                        payload.profileId,
                        payload.contextEntityType,
                        payload.contextEntityId
                    )
                    .pipe(
                        map((response) => {
                            return PermissionsManagerActions.fetchSummarySuccess(
                                {
                                    payload: response,
                                }
                            );
                        }),
                        catchError((response: BadResponseWithError) => {
                            this.notificationsService.showError(
                                response.error?.message ||
                                    'Failed to fetch summary data'
                            );
                            return of(
                                PermissionsManagerActions.fetchSummaryFailure({
                                    payload: response,
                                })
                            );
                        })
                    );
            })
        )
    );

    constructor(
        private actions$: Actions,
        private notificationsService: NotificationsService,
        private permissionsManagerService: PermissionsManagerService
    ) {}
}
